import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, LinearProgress
} from '@mui/material';
import {
  Send as SendIcon, Check as CheckIcon, Close as CloseIcon,
  Person as PersonIcon, AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';

const TransferApprovalAdmin = () => {
  const queryClient = useQueryClient();
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const { data: pendingTransfers, isLoading, error } = useQuery(
    'adminPendingTransfers',
    async () => {
      const response = await apiClient.get('/api/admin/transfers/pending');
      return response.data;
    },
    {
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Admin Transfer Approvals API Error:', error);
      }
    }
  );

  const adminApprovalMutation = useMutation(
    async ({ transferId, action, adminNotes }) => {
      const response = await apiClient.post(`/api/admin/transfers/${transferId}/approve`, {
        action,
        admin_notes: adminNotes
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminPendingTransfers');
        setDialogOpen(false);
        setSelectedTransfer(null);
        setAdminNotes('');
      },
      onError: (error) => {
        console.error('Admin approval failed:', error);
        alert('Failed to process admin approval: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const handleAdminAction = (action) => {
    if (selectedTransfer) {
      adminApprovalMutation.mutate({
        transferId: selectedTransfer.id,
        action,
        adminNotes
      });
    }
  };

  const openDialog = (transfer) => {
    setSelectedTransfer(transfer);
    setDialogOpen(true);
  };

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading pending transfers...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load pending transfers: {error.message}
        </Alert>
      </Box>
    );
  }

  const transfers = pendingTransfers?.transfers || [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{
        color: '#FFFFFF',
        fontWeight: 700,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <SendIcon sx={{ color: '#F7931A' }} />
        Transfer Approval Dashboard
      </Typography>

      <Typography variant="body1" sx={{ color: '#6C7293', mb: 3 }}>
        Review and approve or reject fund transfer requests between users.
      </Typography>

      {transfers.length === 0 ? (
        <Card sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <SendIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
              No Pending Transfers
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293' }}>
              All transfer requests have been processed.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {transfers.map((transfer) => (
            <ListItem
              key={transfer.id}
              sx={{
                border: '1px solid #2B3139',
                borderRadius: 1,
                mb: 2,
                background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
                '&:hover': {
                  borderColor: '#F7931A'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: '#F7931A' }}>
                  <SendIcon />
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                      {formatPrice(transfer.amount)}
                    </Typography>
                    <Chip
                      label={transfer.destination || transfer.coin || 'Overall Asset'}
                      size="small"
                      sx={{ background: 'rgba(247, 147, 26, 0.2)', color: '#F7931A' }}
                    />
                    <Chip
                      label="Pending"
                      color="warning"
                      size="small"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>From:</strong> {transfer.sender?.name || transfer.sender?.email} ({transfer.sender?.email})
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>To:</strong> {transfer.recipient?.name || transfer.recipient?.email} ({transfer.recipient_email})
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Destination Wallet:</strong> {transfer.destination || transfer.coin || 'Overall Asset'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Description:</strong> {transfer.description || 'No description provided'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Requested:</strong> {new Date(transfer.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                }
              />

              <Button
                variant="contained"
                onClick={() => openDialog(transfer)}
                sx={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                  }
                }}
              >
                Review
              </Button>
            </ListItem>
          ))}
        </List>
      )}

      {/* Admin Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#FFFFFF', background: '#1a1a1a' }}>
          Review Transfer Request
        </DialogTitle>
        <DialogContent sx={{ background: '#1a1a1a', color: '#FFFFFF' }}>
          {selectedTransfer && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Amount:</strong> {formatPrice(selectedTransfer.amount)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>From:</strong> {selectedTransfer.sender?.name || selectedTransfer.sender?.email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>To:</strong> {selectedTransfer.recipient?.name || selectedTransfer.recipient_email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Destination Wallet:</strong> {selectedTransfer.destination || selectedTransfer.coin || 'Overall Asset'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Description:</strong> {selectedTransfer.description || 'No description provided'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Requested:</strong> {new Date(selectedTransfer.created_at).toLocaleString()}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Admin Notes (Optional)"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#6C7293' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                  },
                  '& .MuiInputLabel-root': { color: '#6C7293' },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#1a1a1a', p: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#6C7293' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleAdminAction('rejected')}
            variant="outlined"
            color="error"
            disabled={adminApprovalMutation.isLoading}
            sx={{
              borderColor: '#F84960',
              color: '#F84960',
              '&:hover': {
                borderColor: '#DC2626',
                backgroundColor: 'rgba(248, 73, 96, 0.1)'
              }
            }}
          >
            Reject
          </Button>
          <Button
            onClick={() => handleAdminAction('approved')}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              }
            }}
            disabled={adminApprovalMutation.isLoading}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferApprovalAdmin;

